import cn from "../../utils/cn"
import { parsePhysicians, parseSpecialtyChips } from "./videoMetrics"

const cleanName = (name) =>
  (name || "")
    .split(/\s+/)
    .filter((token) => token && token !== "--")
    .join(" ")

const MatchedPhysicians = ({ detail }) => {
  const physicians = parsePhysicians(detail?.MatchingPhysicians)

  const selectedName = detail?.SelectedPhysicianName || ""
  const selectedNpi = detail?.SelectedPhysicianNpi || ""
  const selectedSpecialties = detail?.SelectedPhysicianSpecialties || ""
  const hasSelected = Boolean(selectedName || selectedNpi)

  const isSelectedPhysician = (physician) =>
    hasSelected &&
    physician.npi === selectedNpi &&
    physician.name === selectedName

  const selectedInList = physicians.some(isSelectedPhysician)
  const orderedPhysicians = [
    ...(hasSelected
      ? selectedInList
        ? physicians.filter(isSelectedPhysician)
        : [
            {
              npi: selectedNpi,
              name: selectedName,
              specialty: selectedSpecialties,
            },
          ]
      : []),
    ...physicians.filter((physician) => !isSelectedPhysician(physician)),
  ]

  const sourceName = cleanName(detail?.SourceReferringPhysicianName)

  return (
    <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
      <div className="mb-3 flex items-center justify-between gap-2">
        <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
          Referring physicians
        </p>
        {orderedPhysicians.length > 0 && (
          <span className="rounded-full bg-white px-2 py-0.5 text-[10px] font-semibold text-gray-500 ring-1 ring-gray-200">
            {orderedPhysicians.length} matched
          </span>
        )}
      </div>

      {sourceName && (
        <div className="mb-3 rounded-lg border border-gray-100 bg-white px-3 py-2 text-sm text-gray-600">
          Source referring name:{" "}
          <span className="font-bold text-gray-900">{sourceName}</span>
        </div>
      )}

      {orderedPhysicians.length > 0 ? (
        <div
          className={cn(
            "overflow-hidden rounded-lg border border-gray-100 bg-white",
            orderedPhysicians.length > 10 && "max-h-72 overflow-y-auto"
          )}
        >
          {orderedPhysicians.map((physician, index) => {
            const isSelected = isSelectedPhysician(physician)
            let chips = parseSpecialtyChips(physician.specialty)
            if (!chips.length && isSelected) {
              chips = parseSpecialtyChips(detail?.SelectedPhysicianSpecialties)
            }

            return (
              <div
                key={`${physician.npi}-${index}`}
                className={cn(
                  "flex flex-wrap items-center justify-between gap-x-3 gap-y-1 px-3 py-1.5",
                  index > 0 && "border-t border-gray-100",
                  isSelected && "bg-green-50"
                )}
              >
                <div className="flex min-w-0 items-baseline gap-2">
                  <span
                    className={cn(
                      "text-xs font-medium",
                      isSelected ? "text-green-800" : "text-gray-800"
                    )}
                  >
                    {cleanName(physician.name) || "—"}
                  </span>
                  {physician.npi && (
                    <span className="font-mono text-[10px] text-gray-400">
                      {physician.npi}
                    </span>
                  )}
                </div>

                {chips.length > 0 && (
                  <div className="flex flex-wrap justify-end gap-1">
                    {chips.map((chip, chipIndex) => (
                      <span
                        key={`${chip}-${chipIndex}`}
                        className={cn(
                          "rounded-full border px-2 py-0.5 text-[10px]",
                          isSelected
                            ? "border-green-200 bg-white text-green-700"
                            : "border-gray-200 bg-gray-50 text-gray-600"
                        )}
                      >
                        {chip}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      ) : (
        <span className="text-xs italic text-gray-400">
          No referring physicians matched.
        </span>
      )}
    </div>
  )
}

export default MatchedPhysicians
